import { memo, useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { CloudUpload, Download, Pen, Trash } from 'lucide-react';

import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { getDownloadURL, uploadFile } from '@/client/media';
import { notify } from '@/components/toast';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import LoadingIndicator from '@/components/ui/LoadingIndicator';
import { TextEllipsis } from '@/components/ui/utils/textEllipsis/textEllipsis';
import type { FileCompactData } from '@/components/ui/widget/views/fileCompact/fileCompact.view';
import type { WidgetData } from '@/components/ui/widget/widget.type';
import { getFileIconStr } from '@/utils/fileTypes';
import { getByPath } from '@/utils/widgets';

type Props = {
  path: string;
  data: WidgetData;
  fileData: FileCompactData | undefined;
  onChange: (data: { path: string; value: unknown }) => void;
  setIsPatched: (isPatched: boolean) => void;
  disabled?: boolean;
  defaultIcon?: string;
};

export const UploadFile = memo(
  ({
    path,
    data,
    fileData,
    onChange,
    setIsPatched,
    disabled = false,
    defaultIcon = ''
  }: Props) => {
    const [isLoading, setIsloading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleNewFile = useCallback(
      async (evt: React.ChangeEvent<HTMLInputElement>) => {
        const inputFile = evt.target.files?.[0];
        if (!inputFile) return;

        await uploadFile(
          inputFile,

          // onSuccess
          file => {
            onChange({ path, value: file });
            setIsloading(false);
          },

          // onError
          () => {
            notify.error('Document could not be uploaded');
          },

          // onProgress
          () => {
            setIsloading(true);
          },

          // Disable encryption
          false
        );
      },
      [path, onChange]
    );

    const removeFile = () => onChange({ path, value: undefined });

    useEffect(() => {
      const currentData = getByPath<FileCompactData>(data, path);
      if (setIsPatched) {
        setIsPatched(currentData !== fileData);
      }
    }, [fileData]);

    const openBrowserFile = () => {
      if (!inputRef.current) {
        notify.error('Error: No file input found');
        Sentry.captureException(
          new Error('No file input found when opening browser file')
        );
        return;
      }
      inputRef.current.click();
    };

    const iconStr = getFileIconStr(
      fileData?.mimetype,
      fileData?.name,
      defaultIcon || 'lucide_FileDown'
    );

    if (disabled)
      return (
        <div
          className={cn(
            'relative flex size-full cursor-pointer items-center px-2.5',
            !fileData && 'cursor-not-allowed'
          )}
        >
          {fileData ? (
            <a
              className="flex h-full grow cursor-pointer flex-row flex-nowrap items-center justify-start overflow-hidden text-foreground"
              href={getDownloadURL(fileData)}
              rel="noreferrer"
              target="_blank"
            >
              <DynamicIcon icon={iconStr} size={24} />
              <TextEllipsis
                className="ml-[5px] grow overflow-hidden underline"
                middleEllipsis
                text={fileData?.name}
              />
            </a>
          ) : null}
        </div>
      );

    return (
      <LoadingIndicator showSuccess={false} spinning={isLoading}>
        <div className="relative flex size-full cursor-pointer items-center px-2.5">
          <input hidden onChange={handleNewFile} ref={inputRef} type="file" />
          {fileData ? (
            <div
              className="flex size-full items-center justify-between gap-1 text-foreground"
              data-cy="upload-file-actions"
            >
              <TextEllipsis
                className="text-xs"
                onClick={openBrowserFile}
                text={fileData.name}
              />
              <TooltipProvider>
                <div className="flex shrink-0 items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger data-cy="upload-file-edit">
                      <Pen
                        className="cursor-pointer text-muted-foreground hover:text-primary"
                        onClick={openBrowserFile}
                        size={15}
                      />
                    </TooltipTrigger>
                    <TooltipContent>Edit file</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger data-cy="upload-file-download">
                      <a
                        href={getDownloadURL(fileData)}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <Download
                          className="cursor-pointer text-muted-foreground hover:text-primary"
                          size={15}
                        />
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>Download file</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger data-cy="upload-file-delete">
                      <Trash
                        className="cursor-pointer text-muted-foreground hover:text-destructive"
                        onClick={removeFile}
                        size={15}
                      />
                    </TooltipTrigger>
                    <TooltipContent>Delete file</TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </div>
          ) : (
            <div
              aria-label="Click to upload a file"
              className="flex size-full items-center gap-2 text-muted-foreground hover:text-primary"
              onClick={openBrowserFile}
            >
              Upload a file
              <CloudUpload size={17} />
            </div>
          )}
        </div>
      </LoadingIndicator>
    );
  }
);
